import type { FraudSession, OrgBranding, PkSession, WeGivePayConfig } from './types'
import { ensureRecaptchaLoaded, generatePayframesRecaptchaToken } from './recaptcha'
import { resolveApiBase } from './environment'

export type { PkSession }

/** Thin client. The SDK only ever calls the PK-gated session endpoint —
 *  charges happen server-side on the embedder's backend using their secret
 *  WeGive API key. The SDK has no `charge` method by design. */
export class WeGiveApi {
  private base: string
  private pk: string
  private mockOrg?: OrgBranding
  private recaptchaSiteKey?: string
  private recaptchaTimeoutMs?: number
  private session: PkSession | null = null
  private inflight: Promise<PkSession> | null = null

  constructor(config: WeGivePayConfig) {
    this.base = resolveApiBase(config)
    this.pk = config.publishableKey
    this.mockOrg = config.mockOrg
    this.recaptchaSiteKey = config.recaptchaSiteKey
    this.recaptchaTimeoutMs = config.recaptchaTimeoutMs

    // Warm up reCAPTCHA the moment the SDK is constructed — same idea as
    // Stripe.js pre-loading Radar fingerprinting on page-load — so the
    // first sessionFor() doesn't pay the script-fetch latency.
    void ensureRecaptchaLoaded(this.recaptchaSiteKey, {
      timeoutMs: this.recaptchaTimeoutMs,
    }).catch(() => undefined)
  }

  /** Exchange the publishable key for a session. Idempotent + de-duped:
   *  concurrent callers share a single in-flight request.
   *
   *  Falls back to `mockOrg` (if configured) when the API is unreachable —
   *  this lets devs render the button before the backend exists. */
  sessionFor(
    email?: string,
    opts: { checkoutAttemptId?: string; force?: boolean } = {},
  ): Promise<PkSession> {
    if (
      this.session &&
      !email &&
      !opts.force &&
      !opts.checkoutAttemptId &&
      !isFraudSessionExpired(this.session)
    ) {
      return Promise.resolve(this.session)
    }
    if (this.inflight && !opts.force && !opts.checkoutAttemptId) return this.inflight

    this.inflight = collectFraudHints(opts.checkoutAttemptId, this.recaptchaSiteKey, this.recaptchaTimeoutMs)
      .then((fraud) =>
        this.json<PkSession>('POST', '/api/pay/sessions', {
          publishable_key: this.pk,
          origin: typeof window !== 'undefined' ? window.location.origin : undefined,
          email,
          fraud,
        }),
      )
      .then((session) => {
        this.session = session
        return session
      })
      .catch((err) => {
        if (this.mockOrg) {
          const fallback: PkSession = {
            organization: this.mockOrg,
            vgsVault: 'mock-vault',
            vgsEnvironment: 'sandbox',
          }
          this.session = fallback
          return fallback
        }
        throw err
      })
      .finally(() => {
        this.inflight = null
      })

    return this.inflight
  }

  async createFraudSession(opts: { checkoutAttemptId?: string } = {}): Promise<FraudSession> {
    const session = await this.sessionFor(undefined, { ...opts, force: true })
    if (!session.fraudSession?.id) {
      throw new WeGivePayError('@wegive/pay: API did not return a fraud session', 502)
    }

    return session.fraudSession
  }

  /** Synchronous read of the cached session (after `sessionFor` resolves). */
  get cachedSession(): PkSession | null {
    return this.session
  }

  /** Synchronous best-guess at the org branding — for immediate render
   *  before any network round-trip. Returns the session's org if cached,
   *  the mockOrg if configured, or null otherwise. */
  get bestKnownBranding(): OrgBranding | null {
    return this.session?.organization ?? this.mockOrg ?? null
  }

  private async json<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-WeGive-Publishable-Key': this.pk,
    }
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    const res = await fetch(`${this.base}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    if (!res.ok) {
      const text = await res.text()
      throw new WeGivePayError(
        parseMessage(text) ?? `${method} ${path} → ${res.status}`,
        res.status,
      )
    }
    return (await res.json()) as T
  }
}

async function collectFraudHints(
  checkoutAttemptId?: string,
  recaptchaSiteKey?: string,
  recaptchaTimeoutMs?: number,
): Promise<Record<string, string>> {
  const recaptchaToken = await generatePayframesRecaptchaToken(recaptchaSiteKey, {
    timeoutMs: recaptchaTimeoutMs,
  }).catch(() => undefined)

  if (typeof window === 'undefined') {
    return compact({ checkoutAttemptId, recaptchaToken })
  }

  const nav = window.navigator
  const screenValue =
    typeof window.screen !== 'undefined'
      ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
      : undefined

  return compact({
    timezone: safeTimezone(),
    language: nav.language,
    screen: screenValue,
    sdk_version: '0.1.0',
    checkoutAttemptId,
    recaptchaToken,
  })
}

function compact(input: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(input).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== '',
    ),
  )
}

function safeTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

function isFraudSessionExpired(session: PkSession): boolean {
  if (!session.fraudSession?.expiresAt) return false
  const expires = Date.parse(session.fraudSession.expiresAt)
  if (!Number.isFinite(expires)) return false

  return expires <= Date.now() + 60_000
}

export class WeGivePayError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
    this.name = 'WeGivePayError'
  }
}

function parseMessage(text: string): string | null {
  try {
    const obj = JSON.parse(text) as { message?: string }
    return obj.message ?? null
  } catch {
    return text || null
  }
}
